/* App shell: brand header, current user, logout, and the routed page. */
import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, useLogout } from '../hooks/queries';
import { useApplyReduceMotion } from '../hooks/useReduceMotion';
import { useApplyTheme } from '../hooks/useTheme';

/** True once the page has scrolled off the top. The header's rule and shadow
 *  only appear then, which is how the marketing site's nav behaves. */
function useScrolled(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

export function Layout() {
  const { user } = useAuth();
  const logout = useLogout();
  const nav = useNavigate();
  const scrolled = useScrolled();
  // Reflect the reduce-motion and theme preferences onto the document root,
  // where global CSS and the theme tokens key off them.
  useApplyReduceMotion();
  useApplyTheme();
  return (
    <div className="min-h-full">
      <header
        className={`sticky top-0 z-20 border-b bg-bg/80 backdrop-blur transition-[border-color,box-shadow] duration-300 ${
          scrolled ? 'border-card-b shadow-elev' : 'border-transparent'
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="brand-mark h-8 w-8" aria-hidden="true"><span>V</span></span>
            <span className="text-lg font-extrabold tracking-tight">VentureWrist</span>
          </Link>
          {user && (
            <div className="flex items-center gap-3 text-sm">
              <span className="hidden text-mut sm:inline">
                {user.name} · <span className="capitalize">{user.role}</span>
              </span>
              <Link to="/settings" className="btn-ghost px-3 py-1.5">Settings</Link>
              <button
                className="btn-ghost px-3 py-1.5"
                onClick={() => logout.mutate(undefined, { onSuccess: () => nav('/login') })}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, status } = useAuth();
  // If we have a session, always render — a transient /auth/me failure (API
  // restart, network blip) must NOT bounce a player out of their game.
  if (user) return children;
  if (status === 'pending') {
    return <div className="grid min-h-screen place-items-center text-mut">Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div className="grid min-h-screen place-items-center gap-3 text-center text-mut">
        <p>Can't reach the server.</p>
        <button className="btn-ghost px-3 py-1.5" onClick={() => location.reload()}>Retry</button>
      </div>
    );
  }
  // Settled with no user → genuinely logged out.
  return <Navigate to="/login" replace />;
}

import { Navigate } from 'react-router-dom';
